import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebRTC } from '../hooks/useWebRTC';
import axios from 'axios';
import { 
  Users, X, Coins, Volume2, VolumeX, 
  Maximize, Clock, Send, Heart, Gift, Share2,
  UserPlus, UserCheck, ChevronRight, Wallet
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '';
const WS_URL = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

/**
 * Floating Hearts Animation
 */
function FloatingHearts({ hearts }) {
  return (
    <div style={{ position: 'absolute', bottom: 140, right: 16, pointerEvents: 'none', zIndex: 15 }}>
      {hearts.map((heart) => (
        <div
          key={heart.id}
          className="floating-heart"
          style={{
            position: 'absolute',
            bottom: 0,
            right: Math.random() * 30,
            fontSize: 28,
          }}
        >
          {heart.emoji}
        </div>
      ))}
    </div>
  );
}

/**
 * Live Comments Component
 */
function LiveComments({ comments }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments]);

  return (
    <div
      ref={scrollRef}
      className="live-comments"
      style={{
        position: 'absolute',
        bottom: 140,
        left: 12,
        right: 80,
        maxHeight: 200,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        zIndex: 10,
      }}
    >
      {comments.slice(-15).map((comment) => (
        <div
          key={comment.id}
          className="comment-bubble"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '6px 10px',
            background: 'rgba(0,0,0,0.5)',
            borderRadius: 16,
            backdropFilter: 'blur(8px)',
            maxWidth: '90%',
          }}
        >
          <div style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${comment.color || '#14b8a6'}, #0d9488)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            color: '#fff',
            flexShrink: 0
          }}>
            {comment.user?.charAt(0) || 'U'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ color: comment.color || '#14b8a6', fontWeight: 600, fontSize: 12 }}>
              {comment.user}
            </span>
            <span style={{ color: '#fff', fontSize: 13, marginLeft: 6 }}>
              {comment.text}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Gift Animation Component
 */
function GiftAnimation({ gift, onComplete }) {
  useEffect(() => {
    const timer = setTimeout(onComplete, 3000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="gift-animation" style={{
      position: 'absolute',
      top: '40%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 100,
      textAlign: 'center'
    }}>
      <div style={{ fontSize: 64, marginBottom: 8 }}>{gift.emoji}</div>
      <div style={{
        background: 'rgba(0,0,0,0.7)',
        padding: '8px 16px',
        borderRadius: 20,
        backdropFilter: 'blur(8px)'
      }}>
        <div style={{ color: '#fbbf24', fontWeight: 700, fontSize: 14 }}>
          {gift.from} sent {gift.name}
        </div>
        <div style={{ color: '#14b8a6', fontSize: 12 }}>
          +{gift.tokens} tokens
        </div>
      </div>
    </div>
  );
}

/**
 * Wallet Panel Component
 */
function WalletPanel({ isOpen, onClose, balance, onTopUp }) {
  const navigate = useNavigate();
  const quickAmounts = [10, 25, 50, 100];

  if (!isOpen) return null;

  return (
    <div 
      className="wallet-overlay"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center'
      }} 
      onClick={onClose}
    >
      <div
        className="wallet-panel"
        style={{
          width: '100%',
          maxWidth: 400,
          background: '#1a1a1a',
          borderRadius: '24px 24px 0 0',
          padding: 24,
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: 0, fontFamily: 'Manrope, sans-serif' }}>
            My Wallet
          </h3>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: '50%',
              background: '#0a0a0a', border: 'none',
              color: '#888', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderRadius: 16,
          padding: 20,
          marginBottom: 20,
          border: '1px solid #333'
        }}>
          <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>Current Balance</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 32, fontWeight: 800, color: '#fbbf24', fontFamily: 'Manrope, sans-serif' }}>
              {balance.toFixed(2)}
            </span>
            <Coins className="w-6 h-6 text-amber-400" />
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#aaa', fontSize: 13, marginBottom: 12 }}>Quick Top Up</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
            {quickAmounts.map(amount => (
              <button
                key={amount}
                onClick={() => onTopUp(amount)}
                style={{
                  padding: '12px 8px',
                  background: '#0a0a0a',
                  border: '1px solid #333',
                  borderRadius: 12,
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                data-testid={`topup-${amount}`}
              >
                +{amount}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => navigate('/wallet')}
          style={{
            width: '100%',
            padding: '14px',
            background: '#14b8a6',
            border: 'none',
            borderRadius: 12,
            color: '#fff',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8
          }}
          data-testid="go-to-wallet"
        >
          <Wallet className="w-5 h-5" />
          Go to Wallet
        </button>
      </div>
    </div>
  );
}

/**
 * Gift Panel Component
 */
function GiftPanel({ isOpen, onClose, onSendGift, balance }) {
  const gifts = [
    { id: 1, name: 'Heart', emoji: '❤️', tokens: 1 },
    { id: 2, name: 'Fire', emoji: '🔥', tokens: 5 },
    { id: 3, name: 'Star', emoji: '⭐', tokens: 10 },
    { id: 4, name: 'Diamond', emoji: '💎', tokens: 25 },
    { id: 5, name: 'Crown', emoji: '👑', tokens: 50 },
    { id: 6, name: 'Rocket', emoji: '🚀', tokens: 100 },
  ];

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center'
      }} 
      onClick={onClose}
    >
      <div
        className="gift-panel"
        style={{
          width: '100%',
          maxWidth: 400,
          background: '#1a1a1a',
          borderRadius: '24px 24px 0 0',
          padding: 24,
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: 0, fontFamily: 'Manrope, sans-serif' }}>
            Send Gift
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#fbbf24', fontWeight: 600, fontSize: 14 }}>
              <Coins className="w-4 h-4 inline mr-1" />{balance.toFixed(2)}
            </span>
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: '50%', background: '#0a0a0a', border: 'none', color: '#888', cursor: 'pointer' }}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {gifts.map(gift => (
            <button
              key={gift.id}
              onClick={() => balance >= gift.tokens && onSendGift(gift)}
              disabled={balance < gift.tokens}
              style={{
                padding: 16,
                background: balance < gift.tokens ? 'rgba(0,0,0,0.3)' : '#0a0a0a',
                border: '1px solid #333',
                borderRadius: 16,
                cursor: balance < gift.tokens ? 'not-allowed' : 'pointer',
                opacity: balance < gift.tokens ? 0.5 : 1,
                transition: 'all 0.2s',
                textAlign: 'center'
              }}
              data-testid={`gift-${gift.id}`}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>{gift.emoji}</div>
              <div style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{gift.name}</div>
              <div style={{ color: '#fbbf24', fontSize: 11 }}>{gift.tokens} <Coins className="w-3 h-3 inline" /></div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ViewerPage() {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const { token, balance, setBalance, isAuthenticated, user } = useAuth();
  
  const [stream, setStream] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [freeTimeRemaining, setFreeTimeRemaining] = useState(60);
  const [isBilling, setIsBilling] = useState(false);
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  const [localBalance, setLocalBalance] = useState(balance);
  const [showWallet, setShowWallet] = useState(false);
  const [showGifts, setShowGifts] = useState(false);
  const [comments, setComments] = useState([]);
  const [hearts, setHearts] = useState([]);
  const [activeGift, setActiveGift] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [isFollowing, setIsFollowing] = useState(false);
  
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const heartId = useRef(0);
  
  const {
    handleOffer,
    handleViewerIceCandidate,
    closeAllConnections
  } = useWebRTC();

  useEffect(() => {
    const fetchStream = async () => {
      try {
        const res = await axios.get(`${API}/streams/${streamId}`);
        setStream(res.data);
        if (res.data.status === 'ended') {
          toast.error('This stream has ended');
          navigate('/live');
        }
      } catch (e) {
        toast.error('Stream not found');
        navigate('/live');
      }
    };
    fetchStream();
  }, [streamId, navigate]);

  useEffect(() => {
    setLocalBalance(balance);
  }, [balance]);

  // Simulate incoming comments
  useEffect(() => {
    if (!isConnected) return;
    const names = ['Sarah', 'Mike', 'Emma', 'John', 'Lisa', 'David', 'Anna', 'Chris'];
    const messages = ['Great stream!', '🔥🔥🔥', 'Love this!', 'Hello!', 'Amazing!', '👏👏', 'Keep going!', 'Hi everyone!'];
    const colors = ['#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316'];
    
    const interval = setInterval(() => {
      if (Math.random() > 0.6) {
        setComments(prev => [...prev.slice(-20), {
          id: Date.now(),
          user: names[Math.floor(Math.random() * names.length)],
          text: messages[Math.floor(Math.random() * messages.length)],
          color: colors[Math.floor(Math.random() * colors.length)]
        }]);
      }
    }, 2500);
    return () => clearInterval(interval);
  }, [isConnected]);

  const handleWsMessage = useCallback(async (data) => {
    switch (data.type) {
      case 'broadcaster_connected':
        toast.success('Broadcaster is live!');
        break;
      case 'offer':
        const answer = await handleOffer(
          data.sdp,
          (candidate) => wsRef.current?.send(JSON.stringify({ type: 'ice_candidate', candidate })),
          (remoteStream) => {
            if (videoRef.current) {
              videoRef.current.srcObject = remoteStream;
              setIsConnected(true);
            }
          }
        );
        wsRef.current?.send(JSON.stringify({ type: 'answer', sdp: answer }));
        break;
      case 'ice_candidate':
        await handleViewerIceCandidate(data.candidate);
        break;
      case 'free_time_update':
        setFreeTimeRemaining(data.seconds_remaining);
        break;
      case 'billing_started':
        setIsBilling(true);
        setFreeTimeRemaining(0);
        toast.info(`Billing started: ${data.tokens_per_minute} tokens/min`);
        break;
      case 'tokens_deducted':
        setLocalBalance(data.new_balance);
        setBalance(data.new_balance);
        toast.success(`-${data.amount} tokens deducted`);
        break;
      case 'insufficient_balance':
        setShowInsufficientModal(true);
        wsRef.current?.close();
        break;
      case 'stream_ended':
        toast.info('The stream has ended');
        navigate('/live');
        break;
      default:
        break;
    }
  }, [handleOffer, handleViewerIceCandidate, setBalance, navigate]);

  useEffect(() => {
    if (!token || !stream || stream.status === 'ended') return;
    
    const wsUrl = `${WS_URL}/ws/view/${streamId}?token=${token}`;
    wsRef.current = new WebSocket(wsUrl);
    
    wsRef.current.onopen = () => console.log('Connected to stream');
    wsRef.current.onmessage = (event) => handleWsMessage(JSON.parse(event.data));
    wsRef.current.onerror = (error) => { console.error('WebSocket error:', error); toast.error('Connection error'); };
    wsRef.current.onclose = () => setIsConnected(false);
    
    return () => { wsRef.current?.close(); closeAllConnections(); };
  }, [token, stream, streamId, handleWsMessage, closeAllConnections]);

  const sendHeart = () => {
    const emojis = ['❤️', '💖', '💕', '💗', '💓'];
    const newHeart = { id: heartId.current++, emoji: emojis[Math.floor(Math.random() * emojis.length)] };
    setHearts(prev => [...prev, newHeart]);
    setTimeout(() => setHearts(prev => prev.filter(h => h.id !== newHeart.id)), 3000);
  };

  const sendComment = () => {
    if (!commentText.trim()) return;
    setComments(prev => [...prev, { id: Date.now(), user: user?.first_name || 'You', text: commentText, color: '#14b8a6' }]);
    setCommentText('');
  };

  const sendGift = async (gift) => {
    if (localBalance < gift.tokens) return;
    setLocalBalance(prev => prev - gift.tokens);
    setBalance(localBalance - gift.tokens);
    setActiveGift({ ...gift, from: user?.first_name || 'You' });
    setShowGifts(false);
    toast.success(`Sent ${gift.name}!`);
  };

  const handleTopUp = (amount) => {
    toast.info(`Top up ${amount} tokens - Redirecting to wallet...`);
    navigate('/wallet');
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current?.requestFullscreen) {
      videoRef.current.requestFullscreen();
    }
  };

  const leaveStream = () => {
    wsRef.current?.close();
    closeAllConnections();
    navigate('/live');
  };

  if (!isAuthenticated) {
    return (
      <div className="h-screen w-full bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-2xl p-8 max-w-md w-full text-center border border-zinc-800">
          <Coins className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Login Required</h2>
          <p className="text-zinc-400 mb-6">Please login to watch live streams</p>
          <button onClick={() => navigate('/live')} className="px-6 py-3 bg-indigo-500 text-white font-bold rounded-xl hover:bg-indigo-600 transition-all" data-testid="login-redirect">Go to Login</button>
        </div>
      </div>
    );
  }

  if (!stream) {
    return (
      <div className="h-screen w-full bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="h-[100dvh] w-full bg-black flex flex-col relative overflow-hidden" data-testid="viewer-page">
      <Toaster position="top-center" theme="dark" />
      
      {/* Video */}
      <div className="flex-1 relative bg-zinc-900">
        {!isConnected && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-zinc-400">Connecting to stream...</p>
            </div>
          </div>
        )}
        
        <video ref={videoRef} autoPlay playsInline className={`w-full h-full object-contain ${!isConnected ? 'opacity-0' : 'opacity-100'}`} data-testid="viewer-video" />
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/50 pointer-events-none" />
        
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 p-3 flex justify-between items-start z-10">
          {/* Left: Back + Broadcaster info */}
          <div className="flex items-center gap-2">
            <button onClick={leaveStream} className="w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white" data-testid="back-button">
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-2 px-2 py-1 bg-black/50 backdrop-blur-sm rounded-full">
              {stream.profile_picture ? (
                <img src={`https://dev.naofan.app/avatars/${stream.profile_picture}`} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                  {stream.broadcaster_name?.charAt(0) || 'N'}
                </div>
              )}
              <div>
                <div className="text-white font-semibold text-sm leading-tight">{stream.broadcaster_name || 'Creator'}</div>
                <div className="flex items-center gap-1 text-zinc-400 text-xs">
                  <Users className="w-3 h-3" />
                  {stream.viewer_count || 0}
                </div>
              </div>
              <button 
                onClick={() => setIsFollowing(!isFollowing)} 
                className={`ml-2 px-3 py-1 rounded-full text-xs font-semibold ${isFollowing ? 'bg-zinc-700 text-zinc-300' : 'bg-rose-500 text-white'}`}
                data-testid="follow-btn"
              >
                {isFollowing ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          {/* Right: Wallet button */}
          <button 
            onClick={() => setShowWallet(true)} 
            className="flex items-center gap-2 px-3 py-2 bg-black/50 backdrop-blur-sm rounded-full border border-amber-500/30"
            data-testid="wallet-button"
          >
            <Coins className="w-4 h-4 text-amber-400" />
            <div className="text-left">
              <div className="text-[9px] text-zinc-500 uppercase">Balance</div>
              <div className="text-amber-400 font-bold text-sm leading-tight">{localBalance.toFixed(2)}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-amber-400" />
          </button>
        </div>
        
        {/* Free time / Billing badge */}
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-10">
          {!isBilling ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 backdrop-blur-sm rounded-full border border-emerald-500/30" data-testid="free-timer">
              <Clock className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 font-mono font-bold">FREE: {freeTimeRemaining}s</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 backdrop-blur-sm rounded-full border border-amber-500/30" data-testid="billing-indicator">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-bold text-sm">{stream.tokens_per_minute} tokens/min</span>
            </div>
          )}
        </div>

        {/* Live Comments */}
        <LiveComments comments={comments} />

        {/* Floating Hearts */}
        <FloatingHearts hearts={hearts} />

        {/* Gift Animation */}
        {activeGift && <GiftAnimation gift={activeGift} onComplete={() => setActiveGift(null)} />}

        {/* Right side action buttons */}
        <div className="absolute right-3 bottom-44 flex flex-col gap-4 items-center z-15">
          <button onClick={sendHeart} className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-2xl" data-testid="heart-btn">❤️</button>
          <button onClick={() => setShowGifts(true)} className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-2xl" data-testid="gift-btn">🎁</button>
          <button onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success('Link copied!'); }} className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center" data-testid="share-btn">
            <Share2 className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
      
      {/* Bottom: Comment input + controls */}
      <div className="absolute bottom-0 left-0 right-0 p-3 z-20">
        {/* Comment input */}
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendComment()}
            placeholder="Say something..."
            className="flex-1 px-4 py-3 bg-black/50 backdrop-blur-sm border border-white/10 rounded-full text-white text-sm outline-none"
            data-testid="comment-input"
          />
          <button onClick={sendComment} className="w-11 h-11 rounded-full bg-indigo-500 flex items-center justify-center" data-testid="send-comment-btn">
            <Send className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Control buttons */}
        <div className="flex items-center justify-center gap-3">
          <button onClick={handleMuteToggle} className={`w-11 h-11 rounded-full flex items-center justify-center ${isMuted ? 'bg-red-500/30 text-red-400' : 'bg-white/10 text-white'}`} data-testid="mute-btn">
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <button onClick={handleFullscreen} className="w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center" data-testid="fullscreen-btn">
            <Maximize className="w-5 h-5" />
          </button>
          <button onClick={leaveStream} className="px-6 py-3 bg-zinc-700 text-white font-bold rounded-full flex items-center gap-2" data-testid="leave-btn">
            <X className="w-5 h-5" />
            Leave
          </button>
        </div>
      </div>

      {/* Wallet Panel */}
      <WalletPanel isOpen={showWallet} onClose={() => setShowWallet(false)} balance={localBalance} onTopUp={handleTopUp} />

      {/* Gift Panel */}
      <GiftPanel isOpen={showGifts} onClose={() => setShowGifts(false)} onSendGift={sendGift} balance={localBalance} />

      {/* Insufficient balance modal */}
      {showInsufficientModal && (
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-250 p-4">
          <div className="bg-zinc-900 rounded-2xl p-6 max-w-sm w-full border border-zinc-800" data-testid="insufficient-modal">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Coins className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Insufficient Balance</h3>
              <p className="text-zinc-400 mb-6">You don't have enough tokens to continue watching.</p>
              <div className="flex gap-3">
                <button onClick={() => navigate('/live')} className="flex-1 px-4 py-3 bg-zinc-800 text-white font-medium rounded-xl">Back</button>
                <button onClick={() => setShowWallet(true)} className="flex-1 px-4 py-3 bg-indigo-500 text-white font-bold rounded-xl">Top Up</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .live-comments { mask-image: linear-gradient(to bottom, transparent 0%, black 20%); -webkit-mask-image: linear-gradient(to bottom, transparent 0%, black 20%); }
        .comment-bubble { animation: slideInLeft 0.3s ease-out; }
        .floating-heart { animation: floatHeart 3s ease-out forwards; opacity: 0; }
        .gift-animation { animation: giftPop 3s ease-out forwards; }
        .wallet-panel, .gift-panel { animation: slideUp 0.3s ease-out; }
        @keyframes slideInLeft { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes floatHeart { 0% { opacity: 1; transform: translateY(0) scale(1); } 100% { opacity: 0; transform: translateY(-200px) scale(1.5); } }
        @keyframes giftPop { 0% { opacity: 0; transform: translate(-50%, -50%) scale(0); } 20% { opacity: 1; transform: translate(-50%, -50%) scale(1.2); } 30% { transform: translate(-50%, -50%) scale(1); } 80% { opacity: 1; } 100% { opacity: 0; } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>
    </div>
  );
}
